import SelectFormField from '@components/Common/SelectFormField';
import { Alert, Box, Button, Typography } from '@mui/material';
import React from 'react';

import SectionEditor from './SectionEditor';
import StructuredRevisionChat from './StructuredRevisionChat';
import { MIRRORED_KEYS, REQUIRED_SECTIONS, SECTION_LABELS, missingRequired, parseGuide } from './sections';
import { FIELD_MAP, collectHeadings, seedFor, structuredOrder, validateStructured } from './structured/fieldMap';
import { StructuredContext } from './structured/StructuredContext';
import './structured/structured.scss';

interface StructuredBodyEditorProps {
  contentJson: string;
  pageType: string;
  slug: string;
  /**
   * Takes an updater, not a value: "add empty sections" adds several in one tick, and each has to
   * see the one before it rather than the render's copy.
   */
  onChange: (update: (prev: string) => string) => void;
}

const write = (prev: string, mutate: (obj: Record<string, unknown>) => void): string => {
  const p = parseGuide(prev);
  if (!p.ok) return prev;
  const next = { ...p.obj };
  mutate(next);
  return JSON.stringify(next, null, 2);
};

/**
 * The body of a template guide, edited field by field.
 *
 * Shared by the import screen and the editor shell, so a guide fresh from the generator and the
 * same guide reopened a week later are edited with the same controls.
 */
const StructuredBodyEditor: React.FC<StructuredBodyEditorProps> = ({ contentJson, pageType, slug, onChange }) => {
  const parsed = React.useMemo(() => parseGuide(contentJson), [contentJson]);
  const obj = parsed.obj;
  const sections = React.useMemo(() => structuredOrder(obj, pageType, MIRRORED_KEYS), [obj, pageType]);
  const missing = React.useMemo(() => missingRequired(obj, pageType), [obj, pageType]);
  const context = React.useMemo(
    () => ({
      slug,
      headings: collectHeadings(obj),
      issues: new Map(validateStructured(obj, pageType).map((i) => [i.path, i])),
    }),
    [obj, pageType, slug],
  );

  const setSection = React.useCallback(
    (name: string, next: unknown) =>
      onChange((prev) =>
        write(prev, (o) => {
          if (next === undefined) delete o[name];
          else o[name] = next;
        }),
      ),
    [onChange],
  );
  const removeSection = React.useCallback(
    (name: string) => onChange((prev) => write(prev, (o) => delete o[name])),
    [onChange],
  );
  const addSection = React.useCallback(
    (name: string) => onChange((prev) => write(prev, (o) => { o[name] = seedFor(pageType, name); })),
    [onChange, pageType],
  );
  // An AI edit returns the COMPLETE new envelope, so it replaces the body outright rather than
  // mutating one key. The confirm step in the chat has already run; this just writes the result.
  const applyRevision = React.useCallback(
    (nextJson: string) => onChange(() => nextJson),
    [onChange],
  );

  if (!parsed.ok) {
    return (
      <Alert severity="error">
        The guide body is not valid JSON, so the section editor cannot open it.
        {parsed.error ? ` ${parsed.error}` : ''}
      </Alert>
    );
  }

  const required = REQUIRED_SECTIONS[pageType] ?? [];
  const addable = Object.entries(FIELD_MAP[pageType] ?? {})
    .filter(([key]) => !(key in obj) && !required.includes(key))
    .map(([key, spec]) => ({ value: key, label: spec.label }));

  return (
    <StructuredContext.Provider value={context}>
      <Box className="ge-structured">
        {sections.map((name) => (
          <SectionEditor
            key={name}
            name={name}
            pageType={pageType}
            value={obj[name]}
            required={required.includes(name)}
            onChange={setSection}
            onRemove={removeSection}
            // Each section can be edited by asking: the chat opens pre-scoped to this key, so the
            // backend reverts anything the edit strays into. The whole envelope is handed to it
            // because the edit returns the whole envelope back.
            revise={{ contentJson, pageType, onRevised: applyRevision }}
          />
        ))}

        {missing.length > 0 && (
          <Box className="ge-missing">
            <Typography className="ge-missing__title">Not in this guide yet</Typography>
            <Box className="ge-missing__row">
              {missing.map((m) => (
                <Button key={m} size="small" variant="outlined" onClick={() => addSection(m)}>
                  Add {FIELD_MAP[pageType]?.[m]?.label ?? SECTION_LABELS[m] ?? m}
                </Button>
              ))}
            </Box>
          </Box>
        )}

        {addable.length > 0 && (
          <Box className="ge-structured__add">
            <SelectFormField required={false}
              name="ge-add-section"
              label="Add a section"
              value=""
              options={[{ value: '', label: 'Choose a section to add' }, ...addable]}
              helperText="Optional sections the template can draw that this guide does not use yet."
              onChange={(_n, v) => v && addSection(String(v))}
            />
          </Box>
        )}

        {/* Keys the inspector owns; the server syncs them into the JSON on save, so editing them
            here would just be overwritten. */}
        <Typography className="ge-mirrored">
          {Array.from(MIRRORED_KEYS).join(', ')} are set in the inspector and written into the body
          on save.
        </Typography>

        {/* Whole-guide AI edit, for cross-cutting changes a single-section edit cannot make (tone,
            repetition across sections, a takeaway that touches several fields). Section-scoped
            edits live on each section card above; this one is deliberately unscoped. */}
        <Box className="ge-structured__ai">
          <Typography className="ge-group-label" component="h3">
            Ask AI to edit the whole guide
          </Typography>
          <StructuredRevisionChat
            contentJson={contentJson}
            pageType={pageType}
            scope={[]}
            onRevised={applyRevision}
          />
        </Box>
      </Box>
    </StructuredContext.Provider>
  );
};

export default StructuredBodyEditor;
